"use client";
import { ReactNode } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { FormInputControllerProps } from "./Common/FormInputControllerProps";
import {
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  SxProps,
  TextFieldVariants,
  Theme,
} from "@mui/material";
import { getFormError } from "./Common/utils";

interface SelectInputProps<T extends FieldValues, Option, ControllerValue>
  extends FormInputControllerProps<T> {
  label?: string;
  options: Option[];
  getOptionLabel: (o: Option) => string | ReactNode;
  mapToControllerValue: (o?: Option) => ControllerValue;
  isOptionEqualToValue: (o: Option, v?: ControllerValue) => boolean;
  FormControlProps?: Omit<FormControlProps, "error">;
  SelectProps?: Omit<SelectProps<number>, "onChange" | "label">;
  sx?: SxProps<Theme>;
  variant?: TextFieldVariants;
  disabled?: boolean;
}

export function SelectInput<T extends FieldValues, Option, ControllerValue>(
  props: SelectInputProps<T, Option, ControllerValue>,
) {
  return (
    <Controller
      {...props.ControllerProps}
      name={props.propertyName}
      control={props.control}
      render={({ field, fieldState, formState: { errors } }) => {
        const formError = getFormError(props.propertyName, errors);
        const optionIndex = props.options.findIndex((o) =>
          props.isOptionEqualToValue(o, field.value),
        );

        return (
          <FormControl
            {...props.FormControlProps}
            error={!!formError}
            sx={{ width: "100%", margin: 1, ...props.FormControlProps?.sx }}
          >
            {props.label ? <InputLabel>{props.label}</InputLabel> : undefined}
            <Select
              variant={props.variant}
              {...props.SelectProps}
              label={props.label}
              name={props.propertyName}
              disabled={props.disabled}
              sx={props.sx}
              value={optionIndex == -1 ? "" : optionIndex}
              onChange={(e) => {
                field.onChange(
                  props.mapToControllerValue(
                    props.options[
                      typeof e.target.value == "string"
                        ? parseInt(e.target.value)
                        : e.target.value
                    ],
                  ),
                );
              }}
            >
              {props.options.map((o, index) => (
                <MenuItem value={index} key={index}>
                  {props.getOptionLabel(o)}
                </MenuItem>
              ))}
            </Select>
            {formError ? (
              <FormHelperText>{formError}</FormHelperText>
            ) : undefined}
          </FormControl>
        );
      }}
    />
  );
}
